import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "grid grid-cols-12 gap-4 h-[calc(100vh-19rem)]" }
const _hoisted_2 = { class: "col-span-12 md:col-span-5 lg:col-span-4 bg-card rounded-lg p-3 overflow-y-auto custumScroll" }
const _hoisted_3 = { class: "text-white font-semibold text-sm mb-3" }
const _hoisted_4 = { class: "grid grid-cols-2 gap-2 mb-3" }
const _hoisted_5 = {
  key: 0,
  class: "text-gray-500 text-sm text-center py-6"
}
const _hoisted_6 = {
  key: 1,
  class: "text-gray-500 text-sm text-center py-6"
}
const _hoisted_7 = { class: "space-y-1" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "flex items-start justify-between gap-2" }
const _hoisted_10 = { class: "flex-1 min-w-0" }
const _hoisted_11 = { class: "text-white text-sm font-medium truncate" }
const _hoisted_12 = { class: "text-xs text-gray-400 truncate" }
const _hoisted_13 = { class: "text-[11px] text-gray-500 mt-0.5" }
const _hoisted_14 = {
  key: 2,
  class: "flex items-center justify-between mt-3 text-xs text-gray-300"
}
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "col-span-12 md:col-span-7 lg:col-span-8 bg-card rounded-lg overflow-hidden" }

import { defineAsyncComponent, ref, onMounted } from "vue";
	import api from "@/api";
	import AsyncLoader from "@/components/widgets/AsyncLoader.vue";

	type HistoryRow = {
		embarkation_id: number;
		embarkation_uuid: string;
		label: string | null;
		embarkation_date: string | null;
		status: string | null;
		status_label: string | null;
		route: string | null;
		vehicle_id: number;
		matriculation: string | null;
		vehicle_label: string | null;
		driver_name: string | null;
	};

	
export default /*#__PURE__*/_defineComponent({
  __name: 'TrackingHistory',
  setup(__props) {

	/**
	 * History tab of the tracking page : lists past embarkations
	 * (status `complete` or `cancelled`) and lets the user replay
	 * any of their GPS trail on the map alongside.
	 *
	 * Endpoint : GET /v1/tracking/history?vehicle_id=&from=&to=&per_page=
	 *   → paginated list (controller-side filters; we just pass them through)
	 *
	 * UX : sidebar = filters + list, main panel = the trail map (replay).
	 * Clicking a row swaps the embarkationId on the map without remount.
	 */
	const EmbarkationTrailMap = defineAsyncComponent({
		loader: () =>
			import(/* webpackChunkName: "embarkation-trail-map" */ "@/components/maps/EmbarkationTrailMap.vue"),
		delay: 200,
		timeout: 30000,
		loadingComponent: AsyncLoader,
	});

	const rows = ref<HistoryRow[]>([]);
	const meta = ref<{ current_page: number; last_page: number; per_page: number; total: number }>({
		current_page: 1,
		last_page: 1,
		per_page: 15,
		total: 0,
	});
	const isLoading = ref(false);
	const filters = ref<{ vehicle_id: string; from: string; to: string }>({
		vehicle_id: "",
		from: "",
		to: "",
	});
	const selectedEmbarkationId = ref<number | null>(null);

	const fetchHistory = async (page = 1) => {
		isLoading.value = true;
		try {
			const params: Record<string, string | number> = { per_page: meta.value.per_page, page };
			if (filters.value.vehicle_id) params.vehicle_id = Number(filters.value.vehicle_id);
			if (filters.value.from) params.from = filters.value.from;
			if (filters.value.to) params.to = filters.value.to;

			const r: any = await api().vehiclePosition.history(params);
			const payload = r?.data || r;
			rows.value = payload?.data || [];
			meta.value = payload?.meta || meta.value;

			// Auto-select first row so the map isn't empty when the page lands.
			if (selectedEmbarkationId.value == null && rows.value.length > 0) {
				selectedEmbarkationId.value = rows.value[0].embarkation_id;
			}
		} catch (e) {
			console.warn("[tracking-history] fetch failed", e);
		} finally {
			isLoading.value = false;
		}
	};

	const select = (row: HistoryRow) => {
		selectedEmbarkationId.value = row.embarkation_id;
	};

	const applyFilters = () => {
		selectedEmbarkationId.value = null;
		fetchHistory(1);
	};

	const goToPage = (page: number) => {
		if (page < 1 || page > meta.value.last_page) return;
		fetchHistory(page);
	};

	const formatDate = (iso: string | null) => {
		if (!iso) return "—";
		try {
			return new Date(iso).toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" });
		} catch {
			return iso;
		}
	};

	const statusBadge = (status: string | null) => {
		if (status === "complete") return "bg-green-600/20 text-green-300";
		if (status === "cancelled") return "bg-red-600/20 text-red-300";
		return "bg-gray-600/20 text-gray-300";
	};

	onMounted(() => {
		fetchHistory(1);
	});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("aside", _hoisted_2, [
      _createElementVNode("p", _hoisted_3, " Embarquements terminés (" + _toDisplayString(meta.value.total) + ") ", 1),
      _createElementVNode("div", _hoisted_4, [
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.from) = $event)),
          type: "date",
          class: "bg-primary text-white text-xs rounded-md px-2 py-1.5 col-span-1 focus:outline-none",
          placeholder: "Du"
        }, null, 512), [
          [_vModelText, filters.value.from]
        ]),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.to) = $event)),
          type: "date",
          class: "bg-primary text-white text-xs rounded-md px-2 py-1.5 col-span-1 focus:outline-none",
          placeholder: "Au"
        }, null, 512), [
          [_vModelText, filters.value.to]
        ]),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.vehicle_id) = $event)),
          type: "number",
          class: "bg-primary text-white text-xs rounded-md px-2 py-1.5 col-span-2 focus:outline-none",
          placeholder: "ID véhicule (optionnel)"
        }, null, 512), [
          [_vModelText, filters.value.vehicle_id]
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "col-span-2 text-xs bg-[#219152] hover:bg-[#167841] text-white rounded-md py-1.5 transition-colors focus:outline-none",
          onClick: applyFilters
        }, " Appliquer les filtres ")
      ]),
      (isLoading.value && rows.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Chargement… "))
        : (rows.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Aucun embarquement terminé sur cette période. "))
          : _createCommentVNode("", true),
      _createElementVNode("ul", _hoisted_7, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
          return (_openBlock(), _createElementBlock("li", {
            key: row.embarkation_id,
            class: _normalizeClass(["rounded-md p-2 cursor-pointer transition-colors", 
						selectedEmbarkationId.value === row.embarkation_id
							? 'bg-secondary'
							: 'bg-primary hover:bg-secondary'
					]),
            onClick: ($event: any) => (select(row))
          }, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("p", _hoisted_11, _toDisplayString(row.label || row.matriculation || "Embarquement #" + row.embarkation_id), 1),
                _createElementVNode("p", _hoisted_12, [
                  _createTextVNode(_toDisplayString(row.matriculation || "—") + " ", 1),
                  (row.route)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" · " + _toDisplayString(row.route), 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("p", _hoisted_13, [
                  _createTextVNode(_toDisplayString(formatDate(row.embarkation_date)) + " ", 1),
                  (row.driver_name)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" · " + _toDisplayString(row.driver_name), 1)
                      ], 64))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("span", {
                class: _normalizeClass(["text-[10px] uppercase rounded px-1.5 py-0.5", statusBadge(row.status)])
              }, _toDisplayString(row.status_label || row.status || "—"), 3)
            ])
          ], 10, _hoisted_8))
        }), 128))
      ]),
      (meta.value.last_page > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
            _createElementVNode("button", {
              class: "px-2 py-1 rounded bg-primary hover:bg-secondary disabled:opacity-50 focus:outline-none",
              disabled: meta.value.current_page <= 1 || isLoading.value,
              onClick: _cache[3] || (_cache[3] = ($event: any) => (goToPage(meta.value.current_page - 1)))
            }, " ← Précédent ", 8, _hoisted_15),
            _createElementVNode("span", null, "Page " + _toDisplayString(meta.value.current_page) + " / " + _toDisplayString(meta.value.last_page), 1),
            _createElementVNode("button", {
              class: "px-2 py-1 rounded bg-primary hover:bg-secondary disabled:opacity-50 focus:outline-none",
              disabled: meta.value.current_page >= meta.value.last_page || isLoading.value,
              onClick: _cache[4] || (_cache[4] = ($event: any) => (goToPage(meta.value.current_page + 1)))
            }, " Suivant → ", 8, _hoisted_16)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("section", _hoisted_17, [
      _createVNode(_unref(EmbarkationTrailMap), { embarkationId: selectedEmbarkationId.value }, null, 8, ["embarkationId"])
    ])
  ]))
}
}

})